import React from 'react';

export type WorkspaceStatus =
  | 'uploading'
  | 'incomplete'
  | 'not-transcribed'
  | 'queued'
  | 'transcribing'
  | 'ready'
  | 'failed'
  | 'rejected';

const TONE: Record<WorkspaceStatus, string> = {
  uploading: 'bg-indigo-500/10 text-indigo-300 ring-indigo-500/30',
  incomplete: 'bg-amber-500/10 text-amber-300 ring-amber-500/30',
  'not-transcribed': 'bg-gray-800 text-gray-400 ring-gray-700',
  // Amber, not indigo: nothing is running and nothing is being spent — the job is waiting out a
  // session reset. Sharing the "in flight" colour would read as work already under way.
  queued: 'bg-amber-500/10 text-amber-300 ring-amber-500/30',
  transcribing: 'bg-indigo-500/10 text-indigo-300 ring-indigo-500/30',
  ready: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
  failed: 'bg-red-500/10 text-red-300 ring-red-500/30',
  // Same red as failed — it is a failure, just one whose balance does not come back. The label is
  // what tells the two apart.
  rejected: 'bg-red-500/10 text-red-300 ring-red-500/30',
};

interface StatusChipProps {
  status: WorkspaceStatus;
  label: string;
  /** Adds the small spinner used while something is genuinely in flight. */
  busy?: boolean;
}

/**
 * The one place a row's state is turned into a colour.
 *
 * Row status is the thing a user scans for, so it gets a chip rather than coloured body text —
 * the shape is recognisable before the word is read.
 */
const StatusChip: React.FC<StatusChipProps> = ({status, label, busy = false}) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium
     ring-1 ring-inset whitespace-nowrap ${TONE[status]}`}
  >
    {busy && (
      <span className="w-2.5 h-2.5 rounded-full border-[1.5px] border-current border-t-transparent animate-spin" />
    )}
    {label}
  </span>
);

export default StatusChip;
